import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TableSortLabel,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import { SaveAlt as ExportIcon } from '@mui/icons-material';
import { API_ENDPOINTS } from '../config/api';
import { useAppSettings } from '../context/AppSettingsContext';
import dayjs from 'dayjs';

function getDeliveryAddress(order) {
  if (order.delivery_address && order.delivery_address.trim() !== '') {
    return order.delivery_address;
  }
  const parts = [
    order.delivery_street,
    order.delivery_city,
    order.delivery_state,
    order.delivery_pincode,
    order.delivery_country,
  ];
  const address = parts.filter(Boolean).join(', ');
  return address || 'N/A';
}

function convertOrdersToCSV(orders) {
  const header = [
    'S.No',
    'App User',
    'Payment Terms',
    'Delivery Terms',
    'Delivery Address',
    'Delivery Conditions',
    'Delivery Date',
    'Status',
  ];
  const rows = orders.map((order, idx) => [
    idx + 1,
    order.app_user?.company_name ?? '',
    order.payment_terms ?? '',
    order.delivery_terms ?? '',
    getDeliveryAddress(order),
    order.delivery_conditions ?? '',
    order.delivery_date === 'Within'
      ? `Within (${order.delivery_date_days} days)`
      : order.delivery_date === 'Immediate'
      ? `Immediate (${order.delivery_date_hours} hours)`
      : order.delivery_date ?? '',
    order.status ?? '',
  ]);
  return [header, ...rows]
    .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

const CustomerOrderReport = () => {
  const { deliveryConditions, deliveryDate } = useAppSettings();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState('');
  const [selectedDeliveryTerm, setSelectedDeliveryTerm] = useState('');
  const [selectedDeliveryCondition, setSelectedDeliveryCondition] = useState('');
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [deliveryTerms, setDeliveryTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [appUsers, setAppUsers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');

  // Transform delivery conditions string into array of objects
  const deliveryConditionsOptions = React.useMemo(() => {
    if (!deliveryConditions) return [];
    return deliveryConditions
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim(),
      }))
      .filter((condition) => condition.name);
  }, [deliveryConditions]);

  // Transform delivery date string into array of objects
  const deliveryDateOptions = React.useMemo(() => {
    if (!deliveryDate) return [];
    return deliveryDate
      .split(",")
      .map((condition, index) => ({
        id: `${index + 1}`,
        name: condition.trim(),
      }))
      .filter((condition) => condition.name);
  }, [deliveryDate]);

  // Compute unique status options from orders
  const statusOptions = React.useMemo(() => {
    const statuses = Array.from(new Set(orders.map(order => order.status).filter(Boolean)));
    return statuses;
  }, [orders]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersResponse, paymentTermsResponse, deliveryTermsResponse, appUsersResponse] = await Promise.all([
          axios.get(API_ENDPOINTS.CUSTOMER_ORDERS.BASE),
          axios.get(API_ENDPOINTS.PAYMENT_TERMS.BASE),
          axios.get(API_ENDPOINTS.DELIVERY_TERMS.BASE),
          axios.get(API_ENDPOINTS.APP_USERS.BASE)
        ]);

        const orderData = Array.isArray(ordersResponse.data)
          ? ordersResponse.data
          : Array.isArray(ordersResponse.data.customerOrders)
          ? ordersResponse.data.customerOrders
          : [];
        setOrders(orderData);
        setFilteredOrders(orderData);

        const paymentTermsData = Array.isArray(paymentTermsResponse.data.paymentTerms)
          ? paymentTermsResponse.data.paymentTerms
          : Array.isArray(paymentTermsResponse.data)
          ? paymentTermsResponse.data
          : paymentTermsResponse.data.data || [];
        setPaymentTerms(paymentTermsData);

        const deliveryTermsData = Array.isArray(deliveryTermsResponse.data.deliveryTerms)
          ? deliveryTermsResponse.data.deliveryTerms
          : Array.isArray(deliveryTermsResponse.data)
          ? deliveryTermsResponse.data
          : deliveryTermsResponse.data.data || [];
        setDeliveryTerms(deliveryTermsData);

        // Get unique buyers from orders
        const uniqueBuyers = Array.from(new Set(orderData.map(order => order.app_user?.id)))
          .filter(id => id) // Remove any null/undefined values
          .map(id => {
            const order = orderData.find(order => order.app_user?.id === id);
            return order.app_user;
          })
          .filter(buyer => buyer); // Remove any null/undefined values

        setAppUsers(uniqueBuyers);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = orders;
    // Date filter
    if (fromDate) {
      filtered = filtered.filter((order) => {
        const date = order.created_at || order.order_date;
        return date && dayjs(date).isAfter(dayjs(fromDate).subtract(1, 'day'));
      });
    }
    if (toDate) {
      filtered = filtered.filter((order) => {
        const date = order.created_at || order.order_date;
        return date && dayjs(date).isBefore(dayjs(toDate).add(1, 'day'));
      });
    }
    // Payment terms filter
    if (selectedPaymentTerm) {
      filtered = filtered.filter((order) => 
        order.payment_terms === selectedPaymentTerm
      );
    }
    // Delivery terms filter
    if (selectedDeliveryTerm) {
      filtered = filtered.filter((order) => 
        order.delivery_terms === selectedDeliveryTerm
      );
    }
    // Delivery conditions filter
    if (selectedDeliveryCondition) {
      filtered = filtered.filter((order) => 
        order.delivery_conditions === selectedDeliveryCondition
      );
    }
    // Delivery date filter
    if (selectedDeliveryDate) {
      filtered = filtered.filter((order) => 
        order.delivery_date === selectedDeliveryDate
      );
    }
    // Buyer filter
    if (selectedBuyer) {
      filtered = filtered.filter((order) => order.app_user?.id === selectedBuyer.id);
    }
    // Status filter
    if (selectedStatus) {
      filtered = filtered.filter((order) => order.status === selectedStatus);
    }
    setFilteredOrders(filtered);
  }, [orders, fromDate, toDate, selectedPaymentTerm, selectedDeliveryTerm, selectedDeliveryCondition, selectedDeliveryDate, selectedBuyer, selectedStatus]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredOrders].sort((a, b) => {
      const aValue = a[key] ?? '';
      const bValue = b[key] ?? '';
      return direction === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    setFilteredOrders(sorted);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportCSV = () => {
    try {
      const paginatedOrders =
        rowsPerPage > 0
          ? filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          : filteredOrders;
      const csv = convertOrdersToCSV(paginatedOrders);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer_orders_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (error) {
      setError('Failed to export data. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box component="section" sx={{ mt: 6 }}>
      <Card sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {exportSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully exported {rowsPerPage > 0 ? Math.min(rowsPerPage, filteredOrders.length - page * rowsPerPage) : filteredOrders.length} records to CSV
          </Alert>
        )}

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" m={3}>
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <Autocomplete
            options={appUsers}
            getOptionLabel={(option) => option.company_name || option.contact_person || `User ${option.id}`}
            value={selectedBuyer}
            onChange={(event, newValue) => {
              setSelectedBuyer(newValue);
            }}
            sx={{ minWidth: 200 }}
            renderInput={(params) => <TextField {...params} label="Select Buyer" />}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Payment Terms</InputLabel>
            <Select
              value={selectedPaymentTerm}
              onChange={(e) => setSelectedPaymentTerm(e.target.value)}
              label="Payment Terms"
            >
              <MenuItem value="">All</MenuItem>
              {paymentTerms.map((term) => (
                <MenuItem key={term.id} value={term.name}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Delivery Terms</InputLabel>
            <Select
              value={selectedDeliveryTerm}
              onChange={(e) => setSelectedDeliveryTerm(e.target.value)}
              label="Delivery Terms"
            >
              <MenuItem value="">All</MenuItem>
              {deliveryTerms.map((term) => (
                <MenuItem key={term.id} value={term.name}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Delivery Conditions</InputLabel>
            <Select
              value={selectedDeliveryCondition}
              onChange={(e) => setSelectedDeliveryCondition(e.target.value)}
              label="Delivery Conditions"
            >
              <MenuItem value="">All</MenuItem>
              {deliveryConditionsOptions.map((condition) => (
                <MenuItem key={condition.id} value={condition.name}>
                  {condition.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Delivery Date</InputLabel>
            <Select
              value={selectedDeliveryDate}
              onChange={(e) => setSelectedDeliveryDate(e.target.value)}
              label="Delivery Date"
            >
              <MenuItem value="">All</MenuItem>
              {deliveryDateOptions.map((option) => (
                <MenuItem key={option.id} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(fromDate || toDate || selectedPaymentTerm || selectedDeliveryTerm || selectedDeliveryCondition || selectedDeliveryDate || selectedBuyer || selectedStatus) && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setSelectedPaymentTerm('');
                setSelectedDeliveryTerm('');
                setSelectedDeliveryCondition('');
                setSelectedDeliveryDate('');
                setSelectedBuyer(null);
                setSelectedStatus('');
              }}
            >
              Clear Filters
            </Button>
          )}

          <Box sx={{ ml: 'auto' }}>
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExportCSV}
              disabled={filteredOrders.length === 0}
            >
              Export CSV ({rowsPerPage > 0 ? Math.min(rowsPerPage, filteredOrders.length - page * rowsPerPage) : filteredOrders.length} records)
            </Button>
          </Box>
        </Box>

        {filteredOrders.length === 0 ? (
          <Alert severity="info">No customer orders found.</Alert>
        ) : (
          <>
            <TableContainer className="custom-table" component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      ['S.No', 'sno'],
                      ['Buyer', 'app_user'],
                      ['Payment Terms', 'payment_terms'],
                      ['Delivery Terms', 'delivery_terms'],
                      ['Delivery Address', 'delivery_address'],
                      ['Delivery Conditions', 'delivery_conditions'],
                      ['Delivery Date', 'delivery_date'],
                      ['Status', 'status'],
                    ].map(([label, key]) => (
                      <TableCell
                        key={key}
                        sx={{
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          fontWeight: 'bold',
                        }}
                      >
                        <TableSortLabel
                          active={sortConfig.key === key}
                          direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                          onClick={() => key !== 'sno' && handleSort(key)}
                          sx={{ color: 'inherit' }}
                          hideSortIcon={key === 'sno'}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order, idx) => (
                      <TableRow key={order.id} hover>
                        <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{order.app_user?.company_name || 'N/A'}</TableCell>
                        <TableCell>{order.payment_terms || 'N/A'}</TableCell>
                        <TableCell>{order.delivery_terms || 'N/A'}</TableCell>
                        <TableCell>{getDeliveryAddress(order)}</TableCell>
                        <TableCell>{order.delivery_conditions || 'N/A'}</TableCell>
                        <TableCell>
                          {order.delivery_date === 'Within'
                            ? `Within (${order.delivery_date_days} days)`
                            : order.delivery_date === 'Immediate'
                            ? `Immediate (${order.delivery_date_hours} hours)`
                            : order.delivery_date || 'N/A'}
                        </TableCell>
                        <TableCell>{order.status || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
              colSpan={8}
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                border: "none",
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default CustomerOrderReport;
